import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ExternalLink, Send, Sparkles, X } from "lucide-react";
import api from "../lib/api";

const PAGE_LABELS = {
  "/app": "Tableau de bord",
  "/app/guests": "Registre des invités",
  "/app/rsvp": "Suivi des RSVP",
  "/app/tables": "Plan de table",
  "/app/ceremony": "Cérémonie",
  "/app/timeline": "Planning du mariage",
  "/app/calendar": "Calendrier",
  "/app/budget": "Budget",
  "/app/gifts": "Cadeaux et cagnotte",
  "/app/vendors": "Prestataires",
  "/app/site": "Site de mariage",
  "/app/invitations": "Invitations",
  "/app/team": "Équipe",
  "/app/tasks": "Tâches",
  "/app/documents": "Documents",
};
const CENTRAL_SESSION_KEY = "ceremind_central_ai_session_v2";

function getPageLabel(pathname) {
  return PAGE_LABELS[pathname] || "Espace Ceremind";
}

function actionLabel(action) {
  const labels = {
    update_wedding: "Mettre à jour les informations du mariage",
    set_theme: "Changer le thème du mariage",
    add_programme: "Ajouter des étapes au programme",
    set_programme: "Remplacer le programme actuel",
    add_photo: "Ajouter une photo à la galerie",
    add_guest: "Ajouter un invité",
    add_vendor: "Ajouter un prestataire",
    add_gift: "Ajouter un cadeau ou une cagnotte",
    add_table: "Ajouter une table",
    add_task: "Créer une mission d’équipe",
    add_shared_timeline_event: "Ajouter une étape au planning partagé",
    add_calendar_event: "Ajouter un événement à l’agenda",
    update_invitation_design: "Mettre à jour le design de l’invitation",
    generate_image: "Générer et ajouter une image",
  };
  return labels[action] || "Appliquer une modification";
}

function isTableLayoutRequest(message) {
  return /\b(placer|place|placement|r[ée]organis|d[ée]placer|bouger|ajuster.*table|table.*(?:salle|passage|couloir)|passage.*table|couloir|quinconce)\b/i.test(message);
}

export default function CeremindAssistant() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [applyingMessageId, setApplyingMessageId] = useState(null);
  const scrollRef = useRef(null);
  const sessionRef = useRef(localStorage.getItem(CENTRAL_SESSION_KEY));
  const pageLabel = getPageLabel(location.pathname);

  useEffect(() => {
    if (!open || !sessionRef.current) return;
    api.get(`/ai/sessions/${sessionRef.current}/messages`)
      .then((response) => setMessages(response.data || []))
      .catch(() => {
        // A deleted session should not prevent the assistant from opening.
        localStorage.removeItem(CENTRAL_SESSION_KEY);
        sessionRef.current = null;
        setMessages([]);
      });
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (preset) => {
    const message = (preset || input).trim();
    if (!message || loading) return;
    setInput("");
    setMessages((current) => [...current, { role: "user", content: message }]);
    setLoading(true);
    try {
      const useVerifiedLayout = location.pathname === "/app/tables" && isTableLayoutRequest(message);
      if (useVerifiedLayout) {
        const layoutRequest = new Promise((resolve) => {
          const timeout = window.setTimeout(() => resolve({ status: "error", message: "Le moteur de plan de table n’est pas prêt. Réessayez dans un instant." }), 5000);
          window.dispatchEvent(new CustomEvent("ceremind:table-layout-request", {
            detail: {
              prompt: message,
              onResult: (proposal) => {
                window.clearTimeout(timeout);
                resolve(proposal);
              },
            },
          }));
        });
        // The same central conversation still receives the request, so the
        // wedding planner keeps its memory and master prompt on the table page.
        const [centralResult, layoutResult] = await Promise.allSettled([
          api.post("/ai/chat", {
            session_id: sessionRef.current,
            message,
            page_context: `Les mariés consultent actuellement la page « ${pageLabel} » (${location.pathname}). Ils demandent un placement : l’aperçu doit respecter le moteur géométrique de Ceremind.`,
            execute_actions: false,
          }),
          layoutRequest,
        ]);
        const central = centralResult.status === "fulfilled" ? centralResult.value.data : null;
        const result = layoutResult.status === "fulfilled" ? layoutResult.value : { status: "error", message: "Le moteur de placement n’a pas répondu." };
        if (central?.session_id) {
          sessionRef.current = central.session_id;
          localStorage.setItem(CENTRAL_SESSION_KEY, central.session_id);
        }
        const isProposal = result?.status === "proposal";
        const layoutReply = isProposal
          ? `${result.message || "L’aperçu est prêt."} L’aperçu réel est visible sur le plan. Vérifiez-le puis utilisez « Appliquer » : aucune table n’a encore été déplacée.`
          : (result?.message || "Je n’ai pas pu préparer d’aperçu pour le moment.");
        setMessages((current) => [...current, {
          id: central?.message_id,
          role: "assistant",
          content: central?.reply ? `${central.reply}\n\n${layoutReply}` : layoutReply,
          proposed_actions: central?.proposed_actions || [],
          actions_applied: [],
          error: result?.status === "error" && !central,
        }]);
        return;
      }
      const response = await api.post("/ai/chat", {
        session_id: sessionRef.current,
        message,
        page_context: `Les mariés consultent actuellement la page « ${pageLabel} » (${location.pathname}). Adapte ta réponse à cette page.`,
        execute_actions: false,
      });
      sessionRef.current = response.data.session_id;
      localStorage.setItem(CENTRAL_SESSION_KEY, response.data.session_id);
      setMessages((current) => [...current, {
        id: response.data.message_id,
        role: "assistant",
        content: response.data.reply,
        proposed_actions: response.data.proposed_actions || [],
        actions_applied: [],
      }]);
    } catch (error) {
      const detail = error?.response?.data?.detail || "L’assistant est momentanément indisponible.";
      setMessages((current) => [...current, { role: "assistant", content: detail, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const applyProposal = async (message) => {
    if (!message?.id || applyingMessageId) return;
    setApplyingMessageId(message.id);
    try {
      const response = await api.post(`/ai/messages/${message.id}/apply`, {});
      setMessages((current) => current.map((item) => item.id === message.id ? {
        ...item,
        actions_applied: response.data.actions_applied || [],
        actions: response.data.executed || [],
      } : item));
    } catch (error) {
      setMessages((current) => [...current, {
        role: "assistant",
        error: true,
        content: error?.response?.data?.detail || "La proposition n’a pas pu être appliquée.",
      }]);
    } finally {
      setApplyingMessageId(null);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[80] flex flex-col items-end gap-3 sm:bottom-7 sm:right-7">
      {open && (
        <section className="flex h-[min(620px,calc(100vh-7rem))] w-[min(390px,calc(100vw-2rem))] flex-col overflow-hidden rounded-[28px] border border-white/80 bg-[#fffdf9]/95 shadow-[0_28px_90px_rgba(54,39,27,0.24)] backdrop-blur-xl">
          <header className="flex items-center gap-3 border-b border-[#eadfce] bg-[linear-gradient(135deg,#fff9ee,#f3eadc)] px-5 py-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#8a9a86] text-white shadow-sm"><Sparkles size={18} /></span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8a9a86]">Ceremind Assistant</p>
              <p className="truncate font-serif text-lg text-[#2d261f]">{pageLabel}</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Fermer l’assistant" className="rounded-full p-2 text-[#8b7767] hover:bg-white"><X size={18} /></button>
          </header>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <div className="rounded-2xl bg-[#f7f1e7] p-4 text-sm leading-6 text-[#62564b]">
                Je connais votre mariage et la page ouverte. Demandez-moi ce que vous souhaitez : je peux conseiller, préparer une proposition et agir après votre validation. Vous gardez toujours tous les outils manuels et le dernier mot.
              </div>
            )}
            {messages.map((message, index) => (
              <div key={message.id || `${message.role}-${index}`} className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-6 ${message.role === "user" ? "ml-auto bg-[#2d261f] text-white" : message.error ? "border border-red-200 bg-red-50 text-red-800" : "bg-[#f7f1e7] text-[#493d33]"}`}>
                <div>{message.content}</div>
                {message.role === "assistant" && message.proposed_actions?.length > 0 && (
                  <div className="mt-3 rounded-xl border border-[#d8c9b2] bg-white/80 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a9a86]">Proposition à valider</p>
                    <ul className="mt-2 space-y-1 text-xs text-[#5f5145]">
                      {message.proposed_actions.map((action, actionIndex) => <li key={`${action.action}-${actionIndex}`}>• {actionLabel(action.action)}</li>)}
                    </ul>
                    {(message.actions_applied || []).length === message.proposed_actions.length ? (
                      <p className="mt-3 text-xs font-semibold text-[#5f7d68]">Modifications appliquées.</p>
                    ) : (
                      <button onClick={() => applyProposal(message)} disabled={applyingMessageId === message.id} className="mt-3 w-full rounded-xl bg-[#8a9a86] px-3 py-2 text-xs font-bold text-white disabled:opacity-50">
                        {applyingMessageId === message.id ? "Application…" : "Appliquer ces modifications"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading && <div className="w-fit rounded-2xl bg-[#f7f1e7] px-4 py-3 text-sm text-[#8a9a86]"><span className="animate-pulse">Ceremind réfléchit…</span></div>}
          </div>

          <div className="border-t border-[#eadfce] p-3">
            <div className="mb-2 flex gap-2 overflow-x-auto pb-1">
              {["Que dois-je vérifier ici ?", "Propose-moi les prochaines étapes"].map((prompt) => (
                <button key={prompt} onClick={() => send(prompt)} className="shrink-0 rounded-full border border-[#ddd0bd] px-3 py-1.5 text-[11px] text-[#6d5d4e] hover:bg-[#f7f1e7]">{prompt}</button>
              ))}
            </div>
            <div className="flex items-end gap-2 rounded-2xl border border-[#dcd0bf] bg-white px-3 py-2 focus-within:border-[#8a9a86]">
              <textarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(); } }} rows={1} placeholder="Demandez à Ceremind…" className="max-h-24 min-h-[26px] flex-1 resize-none bg-transparent text-sm outline-none" />
              <button onClick={() => send()} disabled={!input.trim() || loading} aria-label="Envoyer" className="rounded-xl bg-[#8a9a86] p-2 text-white disabled:cursor-not-allowed disabled:opacity-40"><Send size={16} /></button>
            </div>
            <button onClick={() => { setOpen(false); navigate("/app/ai"); }} className="mt-3 flex w-full items-center justify-center gap-2 text-xs font-semibold text-[#766556] hover:text-[#2d261f]">Ouvrir les conversations <ExternalLink size={13} /></button>
          </div>
        </section>
      )}

      <button onClick={() => setOpen((value) => !value)} className="flex items-center gap-2 rounded-full bg-[#2d261f] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_16px_38px_rgba(45,38,31,0.28)] transition hover:-translate-y-0.5 hover:bg-[#493d33]" aria-expanded={open}>
        {open ? <ChevronDown size={18} /> : <Sparkles size={18} />}
        {open ? "Réduire" : "Demander à Ceremind"}
      </button>
    </div>
  );
}
